import { useState } from 'react';
import {
  Button,
  Combobox,
  Drawer,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  useCombobox,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAdminUsersList } from '../../queries/admin-users';
import { useAddProjectMember } from '../../queries/project-members';
import { ApiError } from '../../lib/api-error';
import type { User } from '../../types/api';

interface Props {
  opened: boolean;
  onClose: () => void;
  projectId: string;
  existingMemberIds: Set<string>;
}

export function AddMemberDrawer({ opened, onClose, projectId, existingMemberIds }: Props) {
  const [q, setQ] = useState('');
  const { data: usersData } = useAdminUsersList({ q: q || undefined, is_active: true, limit: 25 });
  const [selected, setSelected] = useState<User | null>(null);
  const [role, setRole] = useState<'moderator' | 'editor' | 'viewer'>('editor');
  const addM = useAddProjectMember(projectId);

  const combobox = useCombobox();

  const candidates = (usersData?.users ?? []).filter((u) => !existingMemberIds.has(u.id));

  async function onAdd() {
    if (!selected) return;
    try {
      await addM.mutateAsync({ user_id: selected.id, role });
      notifications.show({ title: 'Member added', message: selected.username, color: 'pine' });
      setSelected(null);
      setQ('');
      onClose();
    } catch (err) {
      notifications.show({
        title: 'Add failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      padding="md"
      title={<Title order={4}>Add member</Title>}
    >
      <Stack gap="md">
        <Combobox
          store={combobox}
          onOptionSubmit={(val) => {
            const u = candidates.find((c) => c.id === val) ?? null;
            setSelected(u);
            setQ(u?.username ?? '');
            combobox.closeDropdown();
          }}
        >
          <Combobox.Target>
            <TextInput
              label="User"
              placeholder="Search username or email"
              leftSection={<IconSearch size={14} />}
              value={q}
              onChange={(e) => {
                setQ(e.currentTarget.value);
                setSelected(null);
                combobox.openDropdown();
              }}
              onFocus={() => combobox.openDropdown()}
              onBlur={() => combobox.closeDropdown()}
            />
          </Combobox.Target>
          <Combobox.Dropdown>
            <Combobox.Options mah={240} style={{ overflowY: 'auto' }}>
              {candidates.length === 0 && <Combobox.Empty>Type to search users</Combobox.Empty>}
              {candidates.map((u) => (
                <Combobox.Option value={u.id} key={u.id}>
                  <Group justify="space-between">
                    <Text size="sm">{u.username}</Text>
                    <Text size="xs" c="dimmed">
                      {u.email}
                    </Text>
                  </Group>
                </Combobox.Option>
              ))}
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>

        {selected && (
          <Text size="xs" c="dimmed">
            Selected: <b>{selected.username}</b> ({selected.email})
          </Text>
        )}

        <Select
          label="Role"
          data={[
            { value: 'viewer', label: 'Viewer (read-only)' },
            { value: 'editor', label: 'Editor (read/write)' },
            { value: 'moderator', label: 'Moderator (manages members)' },
          ]}
          value={role}
          onChange={(v) => setRole((v ?? 'editor') as 'moderator' | 'editor' | 'viewer')}
        />

        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onAdd} disabled={!selected} loading={addM.isPending}>
            Add member
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
